import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { openApi } from "../../services/api";

// ─── Fetch app name from public settings (no auth required) ───────────────────
async function fetchAppName() {
  try {
    const { data } = await openApi.get("/public-settings/appName");
    return data || "Fenil CRM";
  } catch {
    return "Fenil CRM";
  }
}

const LAST_UPDATED = "23 de abril de 2025";

const buildSections = (appName) => [
  {
    id: "aceitacao",
    title: "1. Aceitação dos Termos",
    content: [
      `Ao acessar ou utilizar o ${appName}, você concorda com estes Termos de Serviço e com a nossa Política de Privacidade. Caso você esteja utilizando a plataforma em nome de uma empresa ou organização, declara ter autoridade legal para vincular essa entidade a estes termos.`,
      `Se você não concordar com qualquer disposição destes termos, não está autorizado a utilizar o ${appName}.`,
    ],
  },
  {
    id: "descricao",
    title: "2. Sobre o ${appName}",
    titleDynamic: true,
    content: [
      `O ${appName} é uma plataforma de gestão de atendimentos e relacionamento com clientes (CRM) que oferece:`,
      `• **Atendimento multicanal:** integração com WhatsApp Business API e conexões adicionais;\n• **Captura de leads via Facebook Lead Ads:** integração com a API do Facebook para importar leads automaticamente de campanhas de anúncio;\n• **Automações de fluxo:** sequências automáticas de mensagens e ações configuráveis;\n• **CRM e gestão de pipeline:** controle de leads e oportunidades em quadro Kanban;\n• **Relatórios e análises:** métricas de atendimento, conversão e desempenho;\n• **Campanhas em massa:** envio de mensagens para listas de contatos.`,
    ],
  },
  {
    id: "cadastro",
    title: "3. Cadastro e Responsabilidades do Usuário",
    content: [
      `Para utilizar o ${appName}, é necessário criar uma conta com informações precisas e completas. Você é responsável por:`,
      `• Manter a confidencialidade das suas credenciais de acesso;\n• Todas as atividades realizadas sob sua conta;\n• Notificar imediatamente qualquer acesso não autorizado;\n• Garantir que todos os usuários da sua organização cumpram estes termos.`,
      `O compartilhamento de credenciais entre múltiplos usuários é proibido. Cada acesso deve ser pessoal e nominado.`,
    ],
  },
  {
    id: "uso-permitido",
    title: "4. Uso Permitido",
    content: [
      `O ${appName} é uma plataforma destinada exclusivamente a **uso empresarial e profissional**. É permitido:`,
      `• Gerenciar atendimentos a clientes da sua empresa;\n• Utilizar a integração com Facebook Lead Ads para capturar e gerenciar leads de suas próprias campanhas;\n• Configurar automações de atendimento e fluxos de mensagens;\n• Exportar dados da sua própria empresa para uso interno;\n• Integrar a plataforma com sistemas de negócio por meio das APIs disponibilizadas.`,
    ],
  },
  {
    id: "uso-proibido",
    title: "5. Uso Proibido",
    content: [
      `É expressamente **proibido** utilizar o ${appName} para:`,
      `• Envio de spam, mensagens não solicitadas ou comunicações enganosas;\n• Violação das Políticas de Uso do WhatsApp Business e das Políticas da Plataforma Meta;\n• Utilização de dados de leads do Facebook para finalidades não autorizadas pelo titular dos dados ou pela Meta;\n• Coleta, compartilhamento ou comercialização de dados pessoais de terceiros em desacordo com a LGPD;\n• Qualquer atividade ilegal, fraudulenta ou que viole direitos de terceiros;\n• Tentativas de acesso não autorizado a contas ou sistemas de outros usuários;\n• Engenharia reversa ou extração do código-fonte da plataforma;\n• Revenda ou sublicenciamento do serviço sem autorização expressa.`,
      `O descumprimento destas proibições poderá resultar na suspensão imediata da conta, sem prejuízo de responsabilização civil e/ou criminal.`,
    ],
  },
  {
    id: "facebook-meta",
    title: "6. Integração com Facebook / Meta — Obrigações do Usuário",
    content: [
      `Ao utilizar o recurso de integração com o Facebook Lead Ads no ${appName}, você concorda com as seguintes obrigações adicionais:`,
      `**Conformidade com as políticas da Meta:** você se compromete a utilizar os dados obtidos via API do Facebook exclusivamente de acordo com os Termos da Plataforma Meta e a Política de Dados do Facebook.`,
      `**Restrições de uso dos dados de leads:** os leads obtidos por meio da permissão \`leads_retrieval\` devem ser utilizados exclusivamente para a finalidade pela qual foram coletados (ou seja, atendimento e acompanhamento dos leads que voluntariamente preencheram os formulários de anúncio). É vedado o uso desses dados para compartilhamento com terceiros, enriquecimento de bases de dados de anúncios ou qualquer finalidade não autorizada.`,
      `**Consentimento dos titulares:** você declara que os formulários de Lead Ads utilizados estão em conformidade com as políticas de privacidade aplicáveis e que os leads consentiram com a coleta e uso de seus dados.`,
      `**Revogação de acesso:** ao desconectar a integração com o Facebook no ${appName} ou ao revogar as permissões concedidas nas configurações do Facebook, o acesso do ${appName} aos dados do Facebook será imediatamente encerrado.`,
    ],
  },
  {
    id: "dados",
    title: "7. Dados e Privacidade",
    content: [
      `O tratamento de dados pessoais e dos dados obtidos via integração com o Facebook é regido pela Política de Privacidade do ${appName}, disponível em:`,
      `📄 /politica-de-privacidade`,
      `Ao utilizar o ${appName}, você reconhece que, como controlador dos dados dos seus clientes e leads, é responsável por garantir as bases legais adequadas para o tratamento desses dados, em conformidade com a LGPD.`,
    ],
  },
  {
    id: "pagamentos",
    title: "8. Planos e Pagamentos",
    content: [
      `O acesso ao ${appName} está condicionado à contratação de um plano ativo. Os planos, valores e recursos disponíveis estão descritos na área de assinatura da plataforma.`,
      `• **Renovação:** os planos são renovados automaticamente ao término de cada período contratado, salvo cancelamento prévio;\n• **Inadimplência:** o acesso pode ser suspenso em caso de atraso no pagamento;\n• **Reembolsos:** analisados caso a caso conforme a política comercial vigente;\n• **Alterações de preço:** comunicadas com pelo menos 30 dias de antecedência.`,
    ],
  },
  {
    id: "propriedade",
    title: "9. Propriedade Intelectual",
    content: [
      `**Plataforma:** todo o código, design, marcas, documentação e demais elementos do ${appName} são propriedade exclusiva dos seus titulares, protegidos pela legislação de propriedade intelectual aplicável.`,
      `**Seus dados:** você mantém a propriedade integral de todos os dados e conteúdos inseridos na plataforma. O ${appName} utiliza esses dados exclusivamente para a prestação dos serviços contratados, sem qualquer direito de propriedade sobre eles.`,
    ],
  },
  {
    id: "disponibilidade",
    title: "10. Disponibilidade e Suporte",
    content: [
      `O ${appName} é operado com o objetivo de máxima disponibilidade, mas não garante disponibilidade ininterrupta. Podem ocorrer interrupções para manutenções programadas (comunicadas com antecedência), atualizações de segurança ou por fatores externos fora do controle da plataforma.`,
      `O suporte técnico é prestado pelos canais disponibilizados na plataforma, com prazos de atendimento conforme o plano contratado.`,
    ],
  },
  {
    id: "responsabilidade",
    title: "11. Limitação de Responsabilidade",
    content: [
      `Na máxima extensão permitida por lei, o ${appName} não se responsabiliza por danos indiretos, incidentais ou consequenciais decorrentes do uso ou da impossibilidade de uso da plataforma, incluindo dados obtidos via Facebook/Meta.`,
      `A responsabilidade total da plataforma, em qualquer circunstância, fica limitada ao valor pago pelo serviço nos últimos 3 meses.`,
    ],
  },
  {
    id: "cancelamento",
    title: "12. Cancelamento e Exclusão de Dados",
    content: [
      `**Cancelamento pelo usuário:** você pode cancelar a assinatura a qualquer momento pela área de assinatura. O acesso permanece ativo até o fim do período pago.`,
      `**Dados após cancelamento:** após o encerramento, você terá 30 dias para exportar seus dados. Após esse prazo, os dados poderão ser excluídos conforme a Política de Privacidade.`,
      `**Dados do Facebook:** a exclusão dos dados obtidos via integração com o Facebook pode ser solicitada a qualquer momento conforme o processo descrito na Política de Privacidade, seção 6.`,
    ],
  },
  {
    id: "lei",
    title: "13. Lei Aplicável",
    content: [
      `Estes Termos de Serviço são regidos pelas leis da República Federativa do Brasil. Eventuais disputas serão submetidas ao foro competente da sede da empresa responsável pela plataforma, com renúncia a qualquer outro foro, por mais privilegiado que seja.`,
    ],
  },
  {
    id: "alteracoes",
    title: "14. Alterações nos Termos",
    content: [
      `O ${appName} pode atualizar estes Termos de Serviço a qualquer momento. Alterações relevantes serão comunicadas com pelo menos 15 dias de antecedência. O uso continuado da plataforma após essa data constitui aceitação dos novos termos.`,
    ],
  },
];

// ─── Component ─────────────────────────────────────────────────────────────────
export default function TermsOfService() {
  const [appName, setAppName] = useState("Fenil CRM");

  useEffect(() => {
    fetchAppName().then(setAppName);
  }, []);

  const sections = buildSections(appName).map(s => ({
    ...s,
    title: s.title.replace("${appName}", appName),
  }));

  return (
    <div style={styles.page}>
      {/* ── Top bar ── */}
      <div style={styles.topBar}>
        <div style={styles.topBarInner}>
          <div style={styles.brand}>
            <div style={styles.logoFallback}>{appName.charAt(0)}</div>
            <span style={styles.brandName}>{appName}</span>
          </div>
          <div style={styles.navLinks}>
            <RouterLink to="/politica-de-privacidade" style={styles.navLink}>
              Política de Privacidade
            </RouterLink>
            <RouterLink to="/termos-de-servico" style={{ ...styles.navLink, ...styles.navActive }}>
              Termos de Serviço
            </RouterLink>
            <RouterLink to="/login" style={styles.loginBtn}>
              ← Entrar na plataforma
            </RouterLink>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <div style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.heroBadge}>Termos de Serviço</div>
          <h1 style={styles.heroTitle}>{appName} — Termos e Condições de Uso</h1>
          <p style={styles.heroMeta}>
            Última atualização: <strong>{LAST_UPDATED}</strong>
            &nbsp;·&nbsp;
            Plataforma: <strong>{appName}</strong>
          </p>
          <p style={styles.heroDesc}>
            Leia com atenção estes termos antes de utilizar o{" "}
            <strong>{appName}</strong>. Ao acessar a plataforma, você concorda com
            as condições descritas abaixo.
          </p>
        </div>
      </div>

      {/* ── Layout ── */}
      <div style={styles.layout}>
        {/* TOC */}
        <aside style={styles.toc}>
          <p style={styles.tocHeading}>Sumário</p>
          {sections.map(s => (
            <a key={s.id} href={`#${s.id}`} style={styles.tocLink} onClick={smoothScroll(`#${s.id}`)}>
              {s.title}
            </a>
          ))}
        </aside>

        <main style={styles.main}>
          {/* Identity card */}
          <div style={styles.identityCard}>
            <div style={styles.identityIcon}>🏢</div>
            <div>
              <p style={styles.identityTitle}>
                Estes Termos de Serviço são aplicáveis ao uso do{" "}
                <strong>{appName}</strong>
              </p>
              <p style={styles.identityDesc}>
                {appName} é uma plataforma de CRM e gestão de atendimentos com
                integração WhatsApp e Facebook Lead Ads. Esta página é pública e
                acessível sem necessidade de login.
              </p>
            </div>
          </div>

          {/* Summary card */}
          <div style={styles.summaryCard}>
            <p style={styles.summaryTitle}>📋 Pontos principais</p>
            <ul style={styles.summaryList}>
              <li>Plataforma para uso empresarial e profissional</li>
              <li>Integração com Facebook Lead Ads: uso restrito às finalidades autorizadas</li>
              <li>Você é controlador dos dados dos seus clientes e leads (LGPD)</li>
              <li>É vedado usar dados do Facebook para fins não autorizados pela Meta</li>
              <li>Planos renovados automaticamente até cancelamento</li>
              <li>Dados disponíveis para exportação por 30 dias após cancelamento</li>
            </ul>
          </div>

          {/* Sections */}
          {sections.map((section) => (
            <div key={section.id} id={section.id} style={styles.section}>
              <h2 style={styles.sectionTitle}>{section.title}</h2>
              {section.content.map((para, i) => (
                <p key={i} style={styles.para}>
                  {renderMarkdown(para)}
                </p>
              ))}
            </div>
          ))}

          {/* Accept note */}
          <div style={styles.acceptCard}>
            <p style={{ margin: 0, fontSize: 13.5, color: "#713f12", textAlign: "center" }}>
              Ao utilizar o <strong>{appName}</strong>, você confirma que leu,
              entendeu e concorda com estes Termos de Serviço e com a{" "}
              <RouterLink to="/politica-de-privacidade" style={{ color: "#92400e", fontWeight: 600 }}>
                Política de Privacidade
              </RouterLink>
              , incluindo as disposições sobre a integração com Facebook / Meta.
            </p>
          </div>
        </main>
      </div>

      {/* ── Footer ── */}
      <footer style={styles.footer}>
        <div style={styles.footerInner}>
          <span style={styles.footerText}>
            © {new Date().getFullYear()} {appName}. Todos os direitos reservados.
          </span>
          <div style={styles.footerLinks}>
            <RouterLink to="/politica-de-privacidade" style={styles.footerLink}>Política de Privacidade</RouterLink>
            <span style={{ color: "#475569" }}>·</span>
            <RouterLink to="/termos-de-servico" style={styles.footerLink}>Termos de Serviço</RouterLink>
            <span style={{ color: "#475569" }}>·</span>
            <RouterLink to="/login" style={styles.footerLink}>Login</RouterLink>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function smoothScroll(id) {
  return (e) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
}

function renderMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return <code key={i} style={styles.code}>{part.slice(1, -1)}</code>;
    }
    if (part.includes("\n•") || part.match(/\n\d+\./)) {
      return (
        <span key={i}>
          {part.split("\n").map((line, j) => (
            <span key={j} style={{ display: "block", marginBottom: 4 }}>{line}</span>
          ))}
        </span>
      );
    }
    return part;
  });
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const BLUE = "#0ea5e9";

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#1e293b",
    lineHeight: 1.75,
  },
  topBar: {
    position: "sticky",
    top: 0,
    zIndex: 200,
    backgroundColor: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid #e2e8f0",
  },
  topBarInner: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "0 24px",
    height: 58,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  brand: { display: "flex", alignItems: "center", gap: 10 },
  logoFallback: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: BLUE,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: 15,
    flexShrink: 0,
  },
  brandName: { fontWeight: 700, fontSize: 15, color: "#0f172a" },
  navLinks: { display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" },
  navLink: {
    fontSize: 13,
    fontWeight: 500,
    color: "#64748b",
    textDecoration: "none",
    borderBottom: "2px solid transparent",
    paddingBottom: 2,
  },
  navActive: { color: BLUE, borderBottomColor: BLUE },
  loginBtn: {
    fontSize: 12,
    fontWeight: 600,
    color: "#fff",
    backgroundColor: BLUE,
    textDecoration: "none",
    padding: "6px 14px",
    borderRadius: 20,
    whiteSpace: "nowrap",
  },
  hero: {
    background: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    padding: "60px 24px 52px",
  },
  heroInner: { maxWidth: 760, margin: "0 auto", textAlign: "center" },
  heroBadge: {
    display: "inline-block",
    backgroundColor: "rgba(255,255,255,0.2)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "4px 14px",
    borderRadius: 20,
    marginBottom: 16,
    border: "1px solid rgba(255,255,255,0.35)",
  },
  heroTitle: {
    fontSize: "clamp(22px, 3.5vw, 34px)",
    fontWeight: 800,
    color: "#fff",
    margin: "0 0 10px",
    lineHeight: 1.25,
  },
  heroMeta: { fontSize: 13, color: "rgba(255,255,255,0.72)", margin: "0 0 14px" },
  heroDesc: { fontSize: 15, color: "rgba(255,255,255,0.9)", margin: 0, lineHeight: 1.65 },
  layout: {
    maxWidth: 1120,
    margin: "0 auto",
    padding: "40px 24px 48px",
    display: "flex",
    gap: 40,
    alignItems: "flex-start",
  },
  toc: {
    width: 210,
    flexShrink: 0,
    position: "sticky",
    top: 72,
    backgroundColor: "#fff",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    padding: "16px 14px",
    display: "flex",
    flexDirection: "column",
    gap: 1,
  },
  tocHeading: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#94a3b8",
    margin: "0 0 8px",
    padding: "0 5px",
  },
  tocLink: {
    fontSize: 12,
    color: "#64748b",
    textDecoration: "none",
    padding: "4px 7px",
    borderRadius: 5,
    display: "block",
    lineHeight: 1.4,
  },
  main: { flex: 1, minWidth: 0 },
  identityCard: {
    display: "flex",
    gap: 14,
    alignItems: "flex-start",
    backgroundColor: "#e0f2fe",
    border: "1px solid #7dd3fc",
    borderRadius: 10,
    padding: "18px 22px",
    marginBottom: 16,
  },
  identityIcon: { fontSize: 28, flexShrink: 0, marginTop: 2 },
  identityTitle: { fontWeight: 700, fontSize: 14, color: "#0c4a6e", margin: "0 0 6px" },
  identityDesc: { fontSize: 13.5, color: "#075985", margin: 0, lineHeight: 1.6 },
  summaryCard: {
    backgroundColor: "#f0fdf4",
    border: "1px solid #86efac",
    borderRadius: 10,
    padding: "16px 22px",
    marginBottom: 20,
  },
  summaryTitle: { fontWeight: 700, fontSize: 14, color: "#14532d", margin: "0 0 10px" },
  summaryList: {
    margin: 0,
    padding: "0 0 0 18px",
    fontSize: 13.5,
    color: "#166534",
    lineHeight: 1.85,
  },
  section: {
    backgroundColor: "#fff",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    padding: "24px 28px",
    marginBottom: 14,
    scrollMarginTop: 76,
  },
  sectionTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 14px",
    paddingBottom: 10,
    borderBottom: "2px solid #f1f5f9",
  },
  para: {
    fontSize: 14,
    color: "#374151",
    marginBottom: 12,
    lineHeight: 1.75,
  },
  code: {
    backgroundColor: "#f1f5f9",
    color: "#0f172a",
    fontFamily: "monospace",
    fontSize: 12,
    padding: "2px 6px",
    borderRadius: 4,
    border: "1px solid #e2e8f0",
  },
  acceptCard: {
    backgroundColor: "#fefce8",
    border: "1px solid #fde68a",
    borderRadius: 10,
    padding: "18px 22px",
    marginTop: 8,
  },
  footer: { backgroundColor: "#0f172a", padding: "22px 24px" },
  footerInner: {
    maxWidth: 1120,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 10,
  },
  footerText: { fontSize: 13, color: "#94a3b8" },
  footerLinks: { display: "flex", alignItems: "center", gap: 8 },
  footerLink: { fontSize: 13, color: "#94a3b8", textDecoration: "none" },
};
